import React, { useRef, useState } from "react";
import { BOT_NAME, DEFAULT_SPACE_SIZE } from "../consts";
import { colorValue } from "../css/color";
import { useScrollRef } from "../jotai/atoms";
import { useBlock, useBlocks, useUserInfos } from "../liveblocks/maps";
import { usePresence } from "../liveblocks/presences";
import { styled } from "../stitches.config";
import { Presence, Size } from "../types";
import {
  gridToPixelPosition,
  gridToPixelSize,
  multiplyPosition,
} from "../utils/gridPosition";
import { useAnimationFrame } from "../utils/useAnimationFrame";

export const SCROLLABLE_W = gridToPixelSize(DEFAULT_SPACE_SIZE).width;
const SCROLLABLE_H = gridToPixelSize(DEFAULT_SPACE_SIZE).height;
const MINIMAP_W = remToPixels(6);
const MINIMAP_RATIO = MINIMAP_W / SCROLLABLE_W;
const MINIMAP_H = MINIMAP_RATIO * SCROLLABLE_H;

const MiniBlock = React.memo(function MiniBlock({
  blockId,
}: {
  blockId: string;
}) {
  const [, liveBlock] = useBlock(blockId);
  const block = liveBlock?.toObject();
  if (!block || !block.position || !block.size) {
    return null;
  }
  const pos = gridToPixelPosition(block.position);
  const size = gridToPixelSize(block.size);
  const x = scaleHorizontal(pos.x);
  const y = scaleVertical(pos.y);
  const w = scaleHorizontal(size.width);
  const h = scaleVertical(size.height);
  if (block.deleted) return null;
  return (
    <MiniBlockDiv
      key={blockId}
      style={{
        width: w,
        height: h,
        transform: `translate(${x}px, ${y}px)`,
      }}
    />
  );
});

function MiniCursors() {
  const [positions] = usePresence<Presence["position"]>("position");
  const [userInfos] = useUserInfos();

  if (!userInfos) {
    return null;
  }

  const positionsWithColors = Object.entries(positions).map(([uid, pos]) => {
    const { x, y } = pos;
    const userColor = userInfos[uid || ""]?.color;
    const color = colorValue(userColor, 80);

    return {
      x: scaleHorizontal(x),
      y: scaleVertical(y),
      color,
      isBot: userInfos[uid || ""]?.name === BOT_NAME,
    };
  });

  return (
    <>
      {positionsWithColors.map(({ x, y, color, isBot }, i) =>
        isBot ? null : (
          <MiniCursor
            key={i}
            style={{
              transform: `translate3d(${x}px, ${y}px, 0) translate(-50%,-50%)`,
              background: color,
            }}
          />
        )
      )}
    </>
  );
}

function MiniViewports() {
  const [viewports] = usePresence<Presence["viewport"]>("viewport");
  const [userInfos] = useUserInfos();

  if (!userInfos) {
    return null;
  }

  const positionsWithColors = Object.entries(viewports).map(
    ([uid, { position, size }]) => {
      const userColor = userInfos[uid || ""]?.color;
      const color = colorValue(userColor, 80, 0.3);

      return {
        x: scaleHorizontal(position.x),
        y: scaleVertical(position.y),
        width: scaleHorizontal(size.width),
        height: scaleVertical(size.height),
        color,
        isBot: userInfos[uid || ""]?.name === BOT_NAME,
      };
    }
  );

  return (
    <>
      {positionsWithColors.map(({ x, y, width, height, color, isBot }, i) =>
        isBot ? null : (
          <MiniViewport
            key={i}
            style={{
              transform: `translate3d(${x}px, ${y}px, 0)`,
              width: width,
              height: height,
              borderColor: color,
              borderWidth: 1,
              borderStyle: "solid",
            }}
          />
        )
      )}
    </>
  );
}

export default React.memo(Minimap);

function Minimap({ windowSize }: { windowSize: Size }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const { blocks } = useBlocks();
  const scroll = useScrollRef();

  useAnimationFrame(() => {
    if (!viewportRef.current || !scroll?.current) {
      return;
    }

    const ww = windowSize.width;
    const wh = windowSize.height;

    const k = MINIMAP_RATIO;

    const pos = multiplyPosition(scroll.current, {
      x: MINIMAP_RATIO,
      y: MINIMAP_RATIO,
    });

    viewportRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
    viewportRef.current.style.width = ww * k + "px";
    viewportRef.current.style.height = wh * k + "px";
  });

  function onScroll(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();

    const scrollXMax = SCROLLABLE_W - window.innerWidth;
    const scrollYMax = SCROLLABLE_H - window.innerHeight;

    // Where you click should be the center of the viewport box, not the top left
    // So offset the scroll target up and to the left by half the viewport box size
    const mouseXCentered =
      e.nativeEvent.offsetX - (windowSize.width * MINIMAP_RATIO) / 2;
    const mouseYCentered =
      e.nativeEvent.offsetY - (windowSize.height * MINIMAP_RATIO) / 2;

    window.scrollTo(
      Math.max(Math.min(mouseXCentered / MINIMAP_RATIO, scrollXMax), 0),
      Math.max(Math.min(mouseYCentered / MINIMAP_RATIO, scrollYMax), 0)
    );
  }

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    setIsMouseDown(true);
    onScroll(e);
  }

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (isMouseDown) onScroll(e);
  }

  return (
    <Container
      className="minimap"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={() => {
        setIsMouseDown(false);
      }}
      onMouseLeave={() => {
        setIsMouseDown(false);
      }}
    >
      {Object.keys(blocks).map((blockId) => (
        <MiniBlock key={blockId} blockId={blockId} />
      ))}
      <MiniCursors />
      <MiniViewports />
    </Container>
  );
}

const Container = styled("div", {
  position: "relative",
  width: MINIMAP_W,
  height: MINIMAP_H,
  background: "rgba(255, 255, 255, 0.8)",
  borderRadius: "8px",
  cursor: "crosshair",
  // quirk: safari require overflow visible for drop-shadow (elevation)
  // to display without being cut
  overflow: "visible",
  backdropFilter: "blur(16px)",
  elevation: 2,
});

const MiniViewport = styled("div", {
  position: "absolute",
  border: "1px solid $transparentBlack25",
  pointerEvents: "none",
  willChange: "transition",
});

const MiniBlockDiv = styled("div", {
  position: "absolute",
  background: "$paper60_20",
  pointerEvents: "none",
});

const MiniCursor = styled("div", {
  position: "absolute",
  borderRadius: "$circle",
  width: "$1",
  height: "$1",
});

export function remToPixels(rem: number) {
  if (typeof window !== "undefined") {
    return (
      rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
    );
  } else {
    return rem;
  }
}

function scaleHorizontal(value: number) {
  return (value / SCROLLABLE_W) * MINIMAP_W;
}

function scaleVertical(value: number) {
  return (value / SCROLLABLE_H) * MINIMAP_H;
}
